import type { ScreenOverride } from './screenContext';

export type AskPromptId =
  | 'status'
  | 'events'
  | 'battery'
  | 'owner'
  | 'alerts'
  | 'email_pdf';

type StarterAskPromptId = Exclude<AskPromptId, 'email_pdf'>;

/** Starter-prompt menu, in display order. `text(subject)` is the turn sent to the agent. */
export const ASK_PROMPTS: {
  id: StarterAskPromptId;
  label: string;
  text: (subject: string) => string;
}[] = [
  { id: 'status', label: 'Status', text: (s) => `What is the status of ${s}?` },
  {
    id: 'events',
    label: 'Recent events',
    text: (s) => `Show recent events for ${s}.`,
  },
  {
    id: 'battery',
    label: 'Battery trend',
    text: (s) => `Show the battery trend for ${s}.`,
  },
  { id: 'owner', label: 'Owner', text: (s) => `Who owns ${s}?` },
  {
    id: 'alerts',
    label: 'Alerts',
    text: (s) => `Are there any active alerts for ${s}?`,
  },
];

export interface AskGritClawSeed {
  override: ScreenOverride;
  sendText?: string;
  displayText?: string;
}

const subjectFor = (selection: Record<string, unknown>): string => {
  if (typeof selection.device_id === 'string') return selection.device_id;
  if (typeof selection.shipment_id === 'string')
    return `shipment ${selection.shipment_id}`;
  if (
    typeof selection.report_id === 'number' &&
    Number.isInteger(selection.report_id)
  ) {
    return `saved report ID ${selection.report_id}`;
  }
  return 'this selection';
};

/**
 * Compute the seed for "Ask GritClaw about this". Always produces an override so the
 * panel opens with screen_context.selection set; when `promptId` is given it also
 * produces the user turn to send. Pure: no React, no provider access.
 */
export const buildAskGritClawSeed = (
  selection: Record<string, unknown>,
  promptId?: AskPromptId
): AskGritClawSeed => {
  const subject = subjectFor(selection);
  const override: ScreenOverride = {
    selection,
    visible_summary: `Asking GritClaw about ${subject}`.slice(0, 500),
  };
  if (!promptId) return { override };
  if (promptId === 'email_pdf') {
    return {
      override,
      sendText: `Generate a PDF for ${subject} and help me email it.`,
      displayText: 'Email report',
    };
  }
  const prompt = ASK_PROMPTS.find((p) => p.id === promptId);
  if (!prompt) return { override };
  return {
    override,
    sendText: prompt.text(subject),
    displayText: prompt.label,
  };
};
